import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import ClientCombobox from '@/components/entities/ClientCombobox';
import api from '@/lib/api';
import { toast } from 'sonner';
import { saveDraft, takeDraft, takeCreatedClientId } from '@/lib/formDraft';
import { Calendar, ArrowLeft, AlertTriangle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const emptyForm = () => ({
  client_name: '', client_email: '', client_id: '', phone: '',
  service_type: 'consultation', appointment_date: '', duration_minutes: 60,
  notes: '', assigned_to: '',
});

const toForm = (a) => ({
  client_name: a.client_name || '', client_email: a.client_email || '', client_id: a.client_id || '',
  phone: a.phone || '', service_type: a.service_type || 'consultation',
  appointment_date: a.appointment_date ? new Date(a.appointment_date).toISOString().slice(0, 16) : '',
  duration_minutes: a.duration_minutes || 60, notes: a.notes || '', assigned_to: a.assigned_to || '',
});

const AppointmentForm = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const editing = !!id;
  const draftKey = editing ? `appointment:${id}` : 'appointment:new';

  const [attorneys, setAttorneys] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  const init = useCallback(async () => {
    try {
      const attorneysRes = await api.get('/attorneys').catch(() => ({ data: [] }));
      setAttorneys(attorneysRes.data);

      const draft = takeDraft(draftKey);
      let base;
      if (draft) base = draft;
      else if (editing) {
        const res = await api.get(`/appointments/${id}`);
        setExisting(res.data);
        base = toForm(res.data);
      } else base = emptyForm();

      const createdId = takeCreatedClientId();
      if (createdId) {
        try {
          const c = (await api.get(`/clients/${createdId}`)).data;
          base = { ...base, client_id: c.id, client_name: c.display_name || '', client_email: c.billing_email || '' };
        } catch { /* leave unlinked */ }
      }
      setForm(base);
    } catch {
      toast.error('Failed to load appointment');
      navigate('/appointments');
    } finally {
      setReady(true);
    }
  }, [draftKey, editing, id, navigate]);

  useEffect(() => {
    if (!loading && !user) { navigate('/login'); return; }
    if (user && !['admin', 'senior_partner'].includes(user.role)) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
    if (user) init();
  }, [user, loading, navigate, init]);

  const linkClient = (clientId, client) =>
    setForm((f) => ({
      ...f,
      client_id: clientId || '',
      client_name: client?.display_name || '',
      client_email: client?.billing_email || '',
    }));

  const createClient = () => {
    saveDraft(draftKey, form);
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/clients/new?returnTo=${returnTo}`);
  };

  const save = async () => {
    setSubmitting(true);
    try {
      const apptId = id || uuidv4();
      const payload = {
        id: apptId,
        ...form,
        appointment_date: new Date(form.appointment_date).toISOString(),
        duration_minutes: parseInt(form.duration_minutes, 10),
        status: existing?.status || 'pending',
        case_id: existing?.case_id || null,
        form_submission_id: existing?.form_submission_id || null,
        outlook_event_id: existing?.outlook_event_id || null,
        created_at: existing?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (editing) {
        await api.put(`/appointments/${apptId}`, payload);
        toast.success('Appointment updated');
      } else {
        await api.post('/appointments', payload);
        toast.success('Appointment created');
      }
      navigate('/appointments');
    } catch {
      toast.error('Failed to save appointment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || !ready) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title={editing ? 'Edit Appointment' : 'New Appointment'}
        icon={Calendar}
        titleTestId="appointment-form-title"
        actions={
          <Button variant="outline" onClick={() => navigate('/appointments')} data-testid="appointment-form-back">
            <ArrowLeft size={18} /> Back to appointments
          </Button>
        }
      />

      <Card className="max-w-lg">
        <CardContent className="p-6 space-y-4" data-testid="appointment-form">
        <div>
          <Label htmlFor="appt-client-combobox">Client *</Label>
          <ClientCombobox value={form.client_id} onChange={linkClient} onCreateNew={createClient} testId="appt-client-combobox" />
          {form.client_id && !form.client_email && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-600" data-testid="appt-no-email-warning">
              <AlertTriangle size={12} /> This client has no email on file — confirmation and cancellation notices can&apos;t be sent.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="appt-phone">Phone</Label>
            <Input id="appt-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
          </div>
          <div>
            <Label htmlFor="appt-service-type">Service Type *</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
              <SelectTrigger id="appt-service-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consultation">Initial Consultation</SelectItem>
                <SelectItem value="follow_up">Follow-up Meeting</SelectItem>
                <SelectItem value="case_review">Case Review</SelectItem>
                <SelectItem value="document_signing">Document Signing</SelectItem>
                <SelectItem value="mediation">Mediation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="appt-datetime">Date &amp; Time *</Label>
            <Input id="appt-datetime" type="datetime-local" value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} data-testid="appt-datetime-input" />
          </div>
          <div>
            <Label htmlFor="appt-duration">Duration (minutes)</Label>
            <Select value={form.duration_minutes.toString()} onValueChange={(v) => setForm({ ...form, duration_minutes: parseInt(v, 10) })}>
              <SelectTrigger id="appt-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="appt-assigned">Assign To</Label>
          <Select value={form.assigned_to || 'unassigned'} onValueChange={(v) => setForm({ ...form, assigned_to: v === 'unassigned' ? '' : v })}>
            <SelectTrigger id="appt-assigned"><SelectValue placeholder="Select attorney" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {attorneys.map((attorney) => (
                <SelectItem key={attorney.id} value={attorney.id}>{attorney.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="appt-notes">Notes</Label>
          <Textarea id="appt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={3} />
        </div>
        <div className="flex gap-4 pt-2">
          <Button variant="outline" onClick={() => navigate('/appointments')} className="flex-1">Cancel</Button>
          <Button onClick={save} className="flex-1 bg-primary text-primary-foreground" disabled={!form.client_id || !form.appointment_date || submitting} data-testid="save-appointment-btn">
            {editing ? 'Update' : 'Create'}
          </Button>
        </div>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default AppointmentForm;
